<template>
	<Header
		title="Apps"
		:is-loading="listState.isLoading.value">
		<Button
			icon="fal fa-plus"
			label="Create App"
			@click="appFormVisible = true" />
	</Header>

	<Container class="apps-list__controls">
		<TableFilters
			v-model="filters"
			placeholder="Search apps..."
			:controls
			:filters
			@updated="handleFilterUpdate" />
	</Container>

	<Container
		v-if="listState.isLoaded.value && listState.list.value.length > 0"
		class="apps-list">
		<router-link
			v-for="app in listState.list.value"
			:key="app.id"
			class="apps-list__card-link"
			:to="{ name: 'apps-details', params: { id: app.id } }">
			<Card class="apps-list__card">
				<template #content>
					<div class="apps-list__card-content">
						<!-- Header Section -->
						<div class="apps-list__card-header">
							<div class="apps-list__card-title-section">
								<h3 class="apps-list__card-title">{{ app.name }}</h3>
							</div>
							<div class="apps-list__card-actions">
								<FontAwesomeIcon
									class="apps-list__card-action-icon"
									icon="fal fa-arrow-right" />
							</div>
						</div>

						<!-- Description Section -->
						<div class="apps-list__card-description">
							<p class="apps-list__card-subtitle">{{ app.description }}</p>
						</div>

						<!-- Tags Section -->
						<div
							v-if="isArchived(app) || isDeployed(app) || isDelivered(app)"
							class="apps-list__card-tags">
							<Tag
								v-if="isArchived(app)"
								class="apps-list__tag apps-list__tag--archived"
								severity="secondary">
								<i
									aria-hidden="true"
									class="fal fa-box-archive apps-list__tag-icon"></i>
								Archived
							</Tag>
							<Tag
								v-if="isDeployed(app)"
								class="apps-list__tag apps-list__tag--deployed"
								severity="success">
								<i
									aria-hidden="true"
									class="fal fa-rocket apps-list__tag-icon"></i>
								Deployed
							</Tag>
							<Tag
								v-if="isDelivered(app)"
								class="apps-list__tag apps-list__tag--delivered"
								severity="info">
								<i
									aria-hidden="true"
									class="fal fa-truck-fast apps-list__tag-icon"></i>
								Delivered
							</Tag>
						</div>

						<!-- Footer Section -->
						<div
							v-if="authStore.user?.id === 1 && app.creator?.id !== authStore.user?.id"
							class="apps-list__card-footer">
							<div class="apps-list__card-creator">
								<i
									aria-hidden="true"
									class="fal fa-user apps-list__creator-icon"></i>
								<span class="apps-list__creator-email">{{ app.creator?.email }}</span>
							</div>
						</div>
					</div>
				</template>
			</Card>
		</router-link>
	</Container>
	<Paginator
		v-if="listState.isLoaded.value && listState.list.value.length > 0"
		class="apps-list__paginator"
		:first="listState.pagination.value.from"
		:rows="listState.pagination.value.per_page"
		:total-records="listState.pagination.value.total"
		@page="updatePage($event.page + 1)" />
	<Container
		v-else-if="listState.isLoaded.value"
		class="welcome">
		<div class="welcome__content">
			<div class="welcome__icon">
				<FontAwesomeIcon icon="fal fa-rocket" />
			</div>
			<h1 class="welcome__heading">Welcome to codecannon!</h1>
			<h2 class="welcome__subheading">To get started, create your first app.</h2>
			<Button
				class="welcome__create-button"
				icon="fal fa-plus"
				label="Create My First App"
				@click="appFormVisible = true" />
		</div>
	</Container>
	<Form
		as-dialog
		should-redirect
		:visible="appFormVisible"
		@close="appFormVisible = false"
		@submit="appFormVisible = false" />
</template>

<script setup lang="ts">
import Header from '@/components/Header.vue'
import { onBeforeMount, ref, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import Container from '@/components/Container.vue'
import Form from './components/List/Form.vue'
import { useAppListState } from '@/models/App/States'
import type { App, AppModel } from '@/models/App/Model'
import Card from 'primevue/card'
import Tag from 'primevue/tag'
import Button from 'primevue/button'
import Paginator from 'primevue/paginator'
import { useAuthStore } from '@/stores/Auth'
import TableFilters from '@/components/filters/TableFilters.vue'
import { FilterControls } from '@/helpers/filters/FilterControls'
import { FilterControl } from '@/helpers/filters/FilterControl'
import { Filters } from '@/helpers/filters/Filters'
import FontAwesomeIcon from '@/components/FontAwesomeIcon.vue'

const authStore = useAuthStore()
const listState = useAppListState()
const router = useRouter()
const route = useRoute()
const { isArchived, isDeployed, isDelivered } = useTags()

listState.defaultParams = {
	with: [
		'latestBuild',
		'latestBuild.latestDeployment',
		'latestBuild.latestCodeDelivery',
		'creator',
	],
	orderBy: 'creator_id',
	order: 'asc',
	per_page: 15,
}

const appFormVisible = ref(false)

// Define available controls
const controls = new FilterControls([
	new FilterControl<AppModel>({
		key: 'deployed',
		name: 'Deployed',
		icon: 'fal fa-rocket',
		queryUpdateHandler(qb) {
			if (this.operator === 'is') {
				return qb.whereHas('latestBuild', (qb) => {
					return qb.whereHas('latestDeployment', (qb) => {
						return qb.where('active', '=', true)
					})
				})
			} else {
				return qb.whereDoesntHave('latestBuild', (qb) => {
					return qb.whereHas('latestDeployment', (qb) => {
						return qb.where('active', '=', true)
					})
				})
			}
		},
	}),
	new FilterControl<AppModel>({
		key: 'delivered',
		name: 'Delivered',
		icon: 'fal fa-truck-fast',
		queryUpdateHandler(qb) {
			if (this.operator === 'is') {
				return qb.whereHas('latestBuild', (qb) => {
					return qb.whereHas('latestCodeDelivery', (qb) => {
						return qb.where('status', '=', 'success')
					})
				})
			} else {
				return qb.whereDoesntHave('latestBuild', (qb) => {
					return qb.whereHas('latestCodeDelivery', (qb) => {
						return qb.where('status', '=', 'success')
					})
				})
			}
		},
	}),
	new FilterControl<AppModel>({
		key: 'archived',
		name: 'Archived',
		icon: 'fal fa-box-archive',
		queryUpdateHandler(qb) {
			return qb.where('archived', this.operator === 'is' ? '=' : '!=', true)
		},
	}),
])

const filters = ref(Filters.fromQuery(route.query as any, controls))

watch(
	() => route.query,
	() => {
		const q = filters.value.toQueryObject()
		// If filter didn't change don't reload data
		if (
			JSON.stringify(q) ===
			JSON.stringify(Filters.fromQuery(route.query as any, controls).toQueryObject())
		)
			return
		filters.value = Filters.fromQuery(route.query as any, controls)
		loadDataFromUrl()
	},
)

onBeforeMount(() => {
	loadDataFromUrl()
})

async function handleFilterUpdate() {
	const q = filters.value.toQueryObject()
	await router.replace({ query: { ...q } })
	loadDataFromUrl()
}

async function loadDataFromUrl() {
	const page = parseInt(route.query.page as string) || 1

	// Load the data
	await listState.getList({
		page,
		search: filters.value.search || undefined,
		filters: [filters.value.getFilter()],
	})

	if (listState.isLoaded.value && listState.pagination.value.last_page < page) {
		updatePage(listState.pagination.value.last_page)
	}
}

async function updatePage(page: number) {
	await router.push({
		query: {
			...route.query,
			page: page,
		},
	})
	loadDataFromUrl()
}

function useTags() {
	function isArchived(app: App) {
		return app.archived
	}

	function isDeployed(app: App) {
		return app.latest_build?.latest_deployment?.active
	}

	function isDelivered(app: App) {
		return app.latest_build?.latest_code_delivery?.status === 'success'
	}

	return {
		isArchived,
		isDeployed,
		isDelivered,
	}
}
</script>

<style lang="scss" scoped>
.apps-list__controls {
	display: flex;
	flex-direction: column;
	gap: 8px;
	padding: 12px 16px 0 16px;

	.apps-list__row {
		display: flex;
		align-items: center;
		gap: 12px;
	}

	.apps-list__search {
		flex: 1; /* grow */
		position: relative;

		.apps-list__search-icon {
			position: absolute;
			left: 12px;
			top: 50%;
			transform: translateY(-50%);
			color: var(--p-text-secondary);
		}

		.apps-list__search-input {
			width: 100%;
			padding-left: 34px;
		}
	}

	.apps-list__chips {
		display: flex;
	}
}

.apps-list {
	display: grid;
	grid-template-columns: 1fr;
	gap: 16px;
	flex: 1;
	align-content: start;
	padding-top: 8px;

	@media (min-width: 768px) {
		grid-template-columns: 1fr 1fr;
	}
	@media (min-width: 1200px) {
		grid-template-columns: 1fr 1fr 1fr;
	}

	.apps-list__card-link {
		text-decoration: none;
		color: inherit;
		display: block;
		height: 100%;
		transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

		&:hover {
			text-decoration: none;
			color: inherit;
		}
	}

	.apps-list__card {
		position: relative;
		height: 100%;
		transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
		border-radius: 12px;
		overflow: hidden;
		background: var(--p-surface-0);
		border: 1px solid var(--p-surface-200);
		box-shadow:
			0 1px 3px 0 rgba(0, 0, 0, 0.1),
			0 1px 2px 0 rgba(0, 0, 0, 0.06);

		@media all {
			background: var(--p-surface-900);
			border-color: var(--p-surface-700);
			box-shadow:
				0 1px 3px 0 rgba(0, 0, 0, 0.3),
				0 1px 2px 0 rgba(0, 0, 0, 0.2);
		}
	}

	.apps-list__card-link:hover .apps-list__card {
		cursor: pointer;
		transform: translateY(-2px);
		box-shadow:
			0 10px 25px -3px rgba(0, 0, 0, 0.1),
			0 4px 6px -2px rgba(0, 0, 0, 0.05);
		border-color: var(--p-primary-300);

		@media all {
			box-shadow:
				0 10px 25px -3px rgba(0, 0, 0, 0.4),
				0 4px 6px -2px rgba(0, 0, 0, 0.3);
			border-color: var(--p-primary-600);
		}

		.apps-list__card-action-icon {
			opacity: 1;
			transform: translateX(0);
		}
	}

	.apps-list__card {
		&:deep(.p-card-body) {
			padding: 0;
			height: 100%;

			.p-card-content {
				padding: 0;
				height: 100%;
			}
		}

		&-content {
			display: flex;
			flex-direction: column;
			height: 100%;
			padding: 24px;
			gap: 16px;
		}

		&-header {
			display: flex;
			justify-content: space-between;
			align-items: flex-start;
		}

		&-title-section {
			flex: 1;
		}

		&-title {
			font-size: 20px;
			font-weight: 600;
			color: var(--p-text-color);
			margin: 0;
			line-height: 1.3;
		}

		&-actions {
			display: flex;
			align-items: center;
		}

		&-action-icon {
			opacity: 0;
			transform: translateX(8px);
			transition: all 0.2s ease;
			color: var(--p-primary-500);
			font-size: 14px;
		}

		&-description {
			flex: 1;
		}

		&-subtitle {
			font-size: 14px;
			color: var(--p-text-secondary);
			margin: 0;
			line-height: 1.5;
			display: -webkit-box;
			-webkit-line-clamp: 3;
			line-clamp: 3;
			-webkit-box-orient: vertical;
			overflow: hidden;
		}

		&-tags {
			display: flex;
			flex-wrap: wrap;
			gap: 8px;
			margin-top: 8px;
		}

		&-footer {
			display: flex;
			justify-content: space-between;
			align-items: center;
			padding-top: 16px;
			border-top: 1px solid var(--p-surface-200);

			@media all {
				border-color: var(--p-surface-700);
			}
		}

		&-creator {
			display: flex;
			align-items: center;
			gap: 6px;
			font-size: 12px;
			color: white;
			background: red;
			padding: 4px 8px;
			border-radius: 5px;
		}

		&-meta {
			display: flex;
			align-items: center;
			gap: 8px;
		}

		&-date {
			font-size: 12px;
			color: var(--p-text-secondary);
		}
	}
}

// Tags
.apps-list__tag {
	font-size: 12px;
	font-weight: 500;
	padding: 4px 8px;
	border-radius: 6px;
	display: flex;
	align-items: center;
	gap: 4px;

	&--archived {
		background: var(--p-surface-100);
		color: var(--p-surface-600);
		border: 1px solid var(--p-surface-200);

		@media all {
			background: var(--p-surface-800);
			color: var(--p-surface-400);
			border-color: var(--p-surface-600);
		}
	}

	&--deployed {
		background: var(--p-green-100);
		color: var(--p-green-700);
		border: 1px solid var(--p-green-200);

		@media all {
			background: var(--p-green-900);
			color: var(--p-green-300);
			border-color: var(--p-green-700);
		}
	}

	&--delivered {
		background: var(--p-blue-100);
		color: var(--p-blue-700);
		border: 1px solid var(--p-blue-200);

		@media all {
			background: var(--p-blue-900);
			color: var(--p-blue-300);
			border-color: var(--p-blue-700);
		}
	}

	&-icon {
		font-size: 10px;
	}
}

.apps-list__creator-icon {
	font-size: 10px;
	color: var(--p-text-secondary);
}

.apps-list__creator-email {
	font-weight: 500;
}

.apps-list__paginator {
	margin-bottom: 10px;
}

.welcome {
	display: flex;
	justify-content: center;
	align-items: center;
	min-height: 60vh;
	padding: 32px 16px;

	.welcome__content {
		display: flex;
		flex-direction: column;
		align-items: center;
		text-align: center;
		max-width: 500px;
	}

	.welcome__icon {
		font-size: 64px;
		color: var(--p-primary-500);
		margin-bottom: 24px;
		opacity: 0.8;
	}

	.welcome__heading {
		font-size: 36px;
		font-weight: 700;
		margin: 0 0 16px 0;
		line-height: 1.2;
		color: var(--p-text-color);
	}

	.welcome__subheading {
		margin: 0 0 32px 0;
		line-height: 1.5;
		font-size: 18px;
		color: var(--p-text-secondary);
	}

	.welcome__create-button {
		padding: 12px 24px;
		font-size: 16px;
		font-weight: 600;
		border-radius: 8px;
		transition: all 0.2s ease;

		&:hover {
			transform: translateY(-1px);
			box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
		}
	}
}
</style>
